import { fetchByIds } from "@/lib/large-org";
import { neutralizeCsvFormula } from "@/lib/csv-safe";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { ShieldCheck, Download, Filter } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/_authenticated/admin/audit")({
  component: AuditLogPage,
  head: () => ({ meta: [{ title: "Audit log — MissionReady" }] }),
});

type Row = {
  id: string;
  organization_id: string | null;
  actor_id: string | null;
  actor_email: string | null;
  action: string;
  target_type: string | null;
  target_id: string | null;
  metadata: Record<string, unknown> | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
};

type Profile = { id: string; email: string | null; full_name: string | null };

const ACTION_PRESETS = [
  "all",
  "member.role_changed",
  "incident.deleted",
  "report.submitted",
  "shift.updated",
  "shift.deleted",
];

function AuditLogPage() {
  const { isPlatformAdmin, currentRole, currentOrg } = useAuth();
  const canView = isPlatformAdmin || currentRole === "org_owner" || currentRole === "admin";

  const [rows, setRows] = useState<Row[]>([]);
  const [profiles, setProfiles] = useState<Record<string, Profile>>({});
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState<string>("all");
  const [actorFilter, setActorFilter] = useState<string>("");
  const [from, setFrom] = useState<string>("");
  const [to, setTo] = useState<string>("");

  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      let q = supabase
        .from("audit_log")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(500);
      if (!isPlatformAdmin && currentOrg) q = q.eq("organization_id", currentOrg.id);
      if (actionFilter !== "all") q = q.eq("action", actionFilter);
      if (from) q = q.gte("created_at", new Date(from).toISOString());
      if (to) q = q.lte("created_at", new Date(to).toISOString());
      const { data } = await q;
      if (cancelled) return;
      const list = (data ?? []) as Row[];
      setRows(list);
      const ids = Array.from(new Set(list.map((r) => r.actor_id).filter(Boolean))) as string[];
      if (ids.length) {
        const { data: pdata } = await fetchByIds<any>("profiles", "id, email, full_name", ids, "id", supabase).then((d) => ({ data: d, error: null }), (e) => ({ data: null, error: e }));
        const map: Record<string, Profile> = {};
        (pdata ?? []).forEach((p: Profile) => { map[p.id] = p; });
        setProfiles(map);
      } else {
        setProfiles({});
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [canView, isPlatformAdmin, currentOrg?.id, actionFilter, from, to]);

  const filtered = useMemo(() => {
    if (!actorFilter.trim()) return rows;
    const q = actorFilter.toLowerCase();
    return rows.filter((r) => {
      const p = r.actor_id ? profiles[r.actor_id] : undefined;
      return (
        p?.email?.toLowerCase().includes(q) ||
        p?.full_name?.toLowerCase().includes(q) ||
        r.actor_email?.toLowerCase().includes(q) ||
        r.actor_id?.toLowerCase().includes(q)
      );
    });
  }, [rows, profiles, actorFilter]);

  const exportCsv = () => {
    const header = ["timestamp", "actor", "action", "target_type", "target_id", "metadata"];
    const lines = [header.join(",")];
    filtered.forEach((r) => {
      const actor = (r.actor_id && profiles[r.actor_id]?.email) || r.actor_email || r.actor_id || "";
      const meta = r.metadata ? JSON.stringify(r.metadata).replace(/"/g, '""') : "";
      lines.push([
        neutralizeCsvFormula(r.created_at),
        neutralizeCsvFormula(actor),
        neutralizeCsvFormula(r.action),
        neutralizeCsvFormula(r.target_type ?? ""),
        neutralizeCsvFormula(r.target_id ?? ""),
        `"${neutralizeCsvFormula(meta)}"`,
      ].join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!canView) {
    return (
      <div className="px-4 py-10 md:px-8">
        <Card className="mx-auto max-w-lg p-8 text-center">
          <ShieldCheck className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
          <h1 className="text-lg font-semibold">Restricted</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            The audit log is reserved for platform admins and organization owners.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 md:px-8 md:py-8">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-primary">Compliance</p>
          <h1 className="font-display text-2xl font-bold md:text-3xl">Audit Log</h1>
          <p className="text-sm text-muted-foreground">
            Significant actions across the platform — role changes, deletions, and report submissions.
          </p>
        </div>
        <Button onClick={exportCsv} variant="outline" size="sm" className="gap-2">
          <Download className="h-4 w-4" /> Export CSV
        </Button>
      </div>

      <Card className="mb-4 p-4">
        <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <Filter className="h-3 w-3" /> Filters
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4">
          <div className="min-w-0">
            <label className="mb-1 block text-xs text-muted-foreground">Action</label>
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger aria-label="Action" className="w-full min-w-0"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ACTION_PRESETS.map((a) => (
                  <SelectItem key={a} value={a}>{a === "all" ? "All actions" : a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-0">
            <label className="mb-1 block text-xs text-muted-foreground">Actor (email/name)</label>
            <Input aria-label="Actor (email/name)" className="w-full min-w-0" value={actorFilter} onChange={(e) => setActorFilter(e.target.value)} placeholder="Search…" />
          </div>
          <div className="min-w-0">
            <label className="mb-1 block text-xs text-muted-foreground">From</label>
            <Input aria-label="From" className="w-full min-w-0 appearance-none" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="min-w-0">
            <label className="mb-1 block text-xs text-muted-foreground">To</label>
            <Input aria-label="To" className="w-full min-w-0 appearance-none" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
        </div>

      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">When</th>
                <th className="px-3 py-2 text-left">Actor</th>
                <th className="px-3 py-2 text-left">Action</th>
                <th className="px-3 py-2 text-left">Target</th>
                <th className="px-3 py-2 text-left">Details</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">No audit entries match your filters.</td></tr>
              )}
              {!loading && filtered.map((r) => {
                const p = r.actor_id ? profiles[r.actor_id] : undefined;
                return (
                  <tr key={r.id} className="border-t border-border align-top">
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground">
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{p?.full_name ?? p?.email ?? r.actor_email ?? "system"}</div>
                      {p?.email && <div className="text-xs text-muted-foreground">{p.email}</div>}
                    </td>
                    <td className="px-3 py-2">
                      <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                        {r.action}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {r.target_type ? <div>{r.target_type}</div> : null}
                      {r.target_id ? <div className="truncate font-mono">{r.target_id.slice(0, 12)}…</div> : null}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {r.metadata && Object.keys(r.metadata).length > 0 ? (
                        <pre className="max-w-md whitespace-pre-wrap break-words text-[11px] text-muted-foreground">
                          {JSON.stringify(r.metadata, null, 0)}
                        </pre>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
